import React, { useState } from 'react';
import { TAG_COLORS, ALL_TAGS } from '../config';
import { agentAPI } from '../api';

export default function RightPanel({ contact, onUpdate, companies, instances }) {
  const [tab, setTab] = useState('info');
  const [togglingAgent, setTogglingAgent] = useState(false);

  if (!contact) return (
    <div style={{ width: 300, background: '#1c1c1f', borderLeft: '1px solid #27272a' }} />
  );

  const company = companies.find(c => c.id === contact.company);
  const instance = instances.find(i => i.id === contact.instance);
  const getInitials = name => name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const handleToggleAgent = async () => {
    setTogglingAgent(true);
    try {
      await agentAPI.toggle(contact.phone, !contact.agentActive);
    } catch (e) {
      // dev mode: toggle locally anyway
    }
    onUpdate(contact.id, { agentActive: !contact.agentActive });
    setTogglingAgent(false);
  };

  const handleTagToggle = (tag) => {
    const tags = contact.tags.includes(tag)
      ? contact.tags.filter(t => t !== tag)
      : [...contact.tags, tag];
    onUpdate(contact.id, { tags });
  };

  const s = {
    panel: {
      width: 300,
      background: '#1c1c1f',
      borderLeft: '1px solid #27272a',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #27272a',
    },
    tab: (active) => ({
      flex: 1,
      padding: '10px 4px',
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 500,
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      color: active ? '#818cf8' : '#71717a',
      borderBottom: `2px solid ${active ? '#6366f1' : 'transparent'}`,
    }),
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    },
    avatar: {
      width: 48, height: 48,
      borderRadius: '50%',
      background: (company?.color || '#6366f1') + '33',
      color: company?.color || '#6366f1',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, fontWeight: 600,
      margin: '0 auto 8px',
    },
    centerText: { textAlign: 'center' },
    nameText: { fontSize: 15, fontWeight: 600, color: '#e4e4e7' },
    subText: { fontSize: 12, color: '#71717a', marginTop: 2 },
    section: {},
    sectionTitle: {
      fontSize: 10,
      fontWeight: 600,
      color: '#52525b',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginBottom: 8,
    },
    card: {
      background: '#18181b',
      border: '1px solid #27272a',
      borderRadius: 8,
      padding: 10,
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 12,
      padding: '5px 0',
      borderBottom: '1px solid #27272a',
    },
    rowLast: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 12,
      padding: '5px 0',
    },
    label: { color: '#71717a' },
    value: { fontWeight: 500, color: '#e4e4e7', fontSize: 11 },
    tagsWrap: { display: 'flex', flexWrap: 'wrap', gap: 5 },
    tag: (active, t) => ({
      padding: '3px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 500,
      cursor: 'pointer',
      background: active ? (TAG_COLORS[t]?.bg || '#27272a') : '#27272a',
      color: active ? (TAG_COLORS[t]?.text || '#a1a1aa') : '#52525b',
      border: active ? '1px solid transparent' : '1px solid #3f3f46',
    }),
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 10,
      background: '#18181b',
      border: '1px solid #27272a',
      borderRadius: 8,
    },
    toggleInfo: {},
    toggleTitle: { fontSize: 13, fontWeight: 500, color: '#e4e4e7' },
    toggleSub: { fontSize: 11, color: '#71717a', marginTop: 2 },
    toggle: (on) => ({
      width: 40, height: 22,
      borderRadius: 11,
      background: on ? '#22c55e' : '#3f3f46',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background 0.2s',
      border: 'none',
      flexShrink: 0,
    }),
    toggleThumb: (on) => ({
      position: 'absolute',
      width: 18, height: 18,
      background: '#fff',
      borderRadius: '50%',
      top: 2,
      left: on ? 20 : 2,
      transition: 'left 0.2s',
    }),
    flowNode: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      padding: '5px 0',
      fontSize: 12,
    },
    flowDot: (color) => ({
      width: 8, height: 8,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
      marginTop: 4,
    }),
    flowText: { color: '#a1a1aa' },
    flowSub: { fontSize: 10, color: '#52525b', marginTop: 1 },
    stageChip: {
      padding: '4px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      background: '#1e3a2f',
      color: '#22c55e',
      border: '1px solid #166534',
      display: 'inline-block',
    },
  };

  const SDR_FLOW_NODES = [
    { label: 'Webhook receptor', sub: 'Evolution → n8n', color: '#3b82f6' },
    { label: 'Redis Buffer', sub: 'Agrupa msgs rápidas', color: '#f59e0b' },
    { label: 'AI Agent (Claude Sonnet)', sub: contact.company === 'perpetue' ? 'Sofia — Perpetue Digital' : 'Anna — IOA Palmas', color: '#8b5cf6' },
    { label: 'Google Calendar / Supabase', sub: 'Tools do agente', color: '#14b8a6' },
    { label: 'Resposta via Evolution', sub: 'Texto, mídia ou áudio', color: '#22c55e' },
    { label: 'Postgres → stage lead', sub: 'Atualiza cadência', color: '#ec4899' },
  ];

  return (
    <div style={s.panel}>
      <div style={s.tabs}>
        {['info', 'agente', 'notas'].map(t => (
          <button key={t} style={s.tab(tab === t)} onClick={() => setTab(t)}>
            {t === 'info' ? 'Contato' : t === 'agente' ? 'Agente IA' : 'Notas'}
          </button>
        ))}
      </div>

      <div style={s.content}>
        {tab === 'info' && (
          <>
            <div style={s.centerText}>
              <div style={s.avatar}>{getInitials(contact.name)}</div>
              <div style={s.nameText}>{contact.name}</div>
              <div style={s.subText}>{company?.name}</div>
              <div style={{ ...s.stageChip, marginTop: 8 }}>{contact.stage}</div>
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Dados</div>
              <div style={s.card}>
                <div style={s.row}><span style={s.label}>Telefone</span><span style={s.value}>{contact.phone}</span></div>
                <div style={s.row}><span style={s.label}>Instância</span><span style={s.value}>{instance?.name}</span></div>
                <div style={s.row}><span style={s.label}>Canal</span><span style={s.value}>{contact.channel}</span></div>
                <div style={s.rowLast}><span style={s.label}>Stage</span><span style={s.value}>{contact.stage}</span></div>
              </div>
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Etiquetas</div>
              <div style={s.tagsWrap}>
                {ALL_TAGS.map(t => (
                  <span key={t} style={s.tag(contact.tags.includes(t), t)} onClick={() => handleTagToggle(t)}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'agente' && (
          <>
            <div style={s.section}>
              <div style={s.sectionTitle}>Status do agente</div>
              <div style={s.toggleRow}>
                <div style={s.toggleInfo}>
                  <div style={s.toggleTitle}>SDR Agent</div>
                  <div style={s.toggleSub}>
                    {contact.agentActive ? 'Respondendo automaticamente' : 'Atendimento manual'}
                  </div>
                </div>
                <button
                  style={s.toggle(contact.agentActive)}
                  onClick={handleToggleAgent}
                  disabled={togglingAgent}
                >
                  <span style={s.toggleThumb(contact.agentActive)} />
                </button>
              </div>
              {togglingAgent && (
                <div style={{ fontSize: 11, color: '#71717a', marginTop: 6, textAlign: 'center' }}>
                  Atualizando Redis...
                </div>
              )}
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Fluxo n8n ativo</div>
              <div style={s.card}>
                {SDR_FLOW_NODES.map((node, i) => (
                  <div key={i} style={s.flowNode}>
                    <div style={s.flowDot(node.color)} />
                    <div>
                      <div style={s.flowText}>{node.label}</div>
                      <div style={s.flowSub}>{node.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Modelo IA</div>
              <div style={s.card}>
                <div style={s.row}><span style={s.label}>Modelo</span><span style={{ ...s.value, color: '#818cf8' }}>Claude Sonnet 4.5</span></div>
                <div style={s.row}><span style={s.label}>Memória</span><span style={s.value}>Redis + Postgres</span></div>
                <div style={s.rowLast}><span style={s.label}>Ferramentas</span><span style={s.value}>Calendar, Supabase, ElevenLabs</span></div>
              </div>
            </div>
          </>
        )}

        {tab === 'notas' && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Anotações internas</div>
            <textarea
              placeholder="Adicione notas sobre este contato..."
              style={{
                width: '100%', minHeight: 120,
                background: '#18181b', border: '1px solid #27272a',
                borderRadius: 8, color: '#e4e4e7', fontSize: 13,
                padding: '8px 10px', resize: 'vertical', outline: 'none',
              }}
            />
            <button style={{
              marginTop: 8, padding: '6px 14px',
              background: '#6366f1', border: 'none',
              borderRadius: 6, color: '#fff', fontSize: 12,
              cursor: 'pointer',
            }}>
              Salvar nota
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
